/**
 * The recording, drawn as stacked waveforms with one shared playhead.
 *
 * WHY HAND-ROLLED AND NOT WAVESURFER. The reference implementation (voice2me)
 * uses wavesurfer.js against ONE stereo file, and its whole trick —
 * `splitChannels` — only works because both voices live in one object. This
 * platform records the two sides of a browser call as two separate objects on
 * purpose (they are different kinds of evidence; see the session page), and
 * wavesurfer cannot draw or play two files as one timeline without a fork or a
 * second instance drifting out of sync. Web Audio can: decode both, draw both,
 * start both `AudioBufferSourceNode`s on the same clock. ~200 lines against a
 * 90 kB dependency that would still need syncing hacks.
 *
 * WHERE THE BYTES COME FROM: `/api/sessions/:id/audio/:track/file` by default,
 * the same-origin stream — NOT the presigned S3 URL, which `fetch()` +
 * `decodeAudioData` cannot use cross-origin without bucket CORS.
 *
 * `trackUrl` OVERRIDES THAT, and exists for one caller: the session viewer, which
 * runs inside a customer's own system with no session cookie. It asks that system
 * for a presigned URL and returns it here — hence the ASYNC signature, since a
 * presign is a round trip and cannot be computed from the track name.
 *
 * 🔴 WHATEVER A CALLER RETURNS MUST BE READABLE BY A CORS FETCH. That is the rule
 * the default encodes and the one an override can break: `decodeAudioData` needs
 * the sample data, so this is a real cross-origin read, not a media-element load.
 * A presigned S3 URL works here ONLY if the bucket sends CORS headers for the
 * embedding origin. If waveforms come back empty with a CORS error in the console
 * and the audio is otherwise fine, that bucket config is the first thing to check
 * — it is infrastructure state this repo cannot see.
 *
 * THE LANES SHARE A CLOCK BUT NOT AN ALIGNMENT GUARANTEE. Both captures start
 * when their pipeline opens, within ~100 ms of each other, and the transcript
 * highlight keyed off this playhead uses the session's own start time — so
 * everything here is display furniture accurate to roughly a word, not a
 * measurement. The measured timeline is in the tech stats drawer.
 *
 * A telephony call is the opposite shape: ONE carrier file, two channels. Same
 * view — the two channels become the two lanes.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ EVERY LANE THIS PLAYER DRAWS IS ASSUMED TO BE THE SAME CONVERSATION,
 * SIMULTANEOUS, STARTING AT THE SAME INSTANT. That assumption is the whole
 * mechanism — one clock, one playhead, every buffer started at the same offset —
 * and it holds for the two sides of a browser call and for the two channels of a
 * carrier file.
 *
 * IT DOES NOT HOLD FOR A TRANSFERRED CALL. Such a session owns TWO carrier
 * recordings: `telephony-dual` (caller ↔ AI) and `telephony-transfer`
 * (caller ↔ human), and the second one BEGINS AFTER THE FIRST ONE ENDED. Handing
 * both to one instance produced two real defects on the first live transfer:
 * both conversations played on top of each other, and the second was drawn from
 * x=0 on the first's axis — a picture claiming the human was speaking while the
 * AI still had the call.
 *
 * So the grouping is a decision the CALLER makes, with `splitRecordingsByLeg`
 * below, and it renders one instance per leg. `playableTracks` refuses to mix
 * them as a backstop, because a caller that forgets gets a wrong picture rather
 * than an error.
 */
import { useEffect, useRef, useState } from 'react'
import { DownloadIcon, PauseIcon, PlayIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
/**
 * THE THREE GROUPING RULES LIVE NEXT DOOR, in a module with no React in it, so
 * `tests/legcheck.mjs` can exercise them rather than reading them. They were
 * inline here until a transferred call proved all three wrong at once — the file
 * they moved to says which three and how.
 */
import { isTwoPartyFile, lanesFor, playableTracks } from './audio-legs'
// Re-exported so a page importing the player does not also have to know where
// the grouping rules live; `audio-legs.ts` is the authority either way.
export { splitRecordingsByLeg, TAKEOVER_TRACK, TRANSFER_TRACK } from './audio-legs'
/**
 * TYPED TO THE THREE FIELDS IT READS, not to `@/api`'s twenty-column
 * `RecordingRow`. Same reasoning as `transcript-thread.tsx`: the session viewer
 * feeds this player from an end-of-call payload, which knows WHICH tracks have
 * objects and nothing else about them, and the seventeen absent columns are not
 * ones a caller may invent — `evidence`, `actualMs` and the expectation figures
 * are the panel's completeness machinery, and a fabricated value there is a
 * fabricated completeness claim.
 *
 * The panel's `RecordingRow` satisfies this structurally, so nothing changed for
 * the session page.
 */
export interface PlayableRecording {
  track: string
  /** Only `'READY'` is played. Typed as `string` so a caller need not import the
   *  panel's `RecordingState` union to say so. */
  state: string
  /** Non-null means the object exists. The value itself is never dereferenced
   *  here — `trackUrl` decides where the bytes actually come from. */
  s3Key: string | null
}

const LANE_H = 56
const AXIS_H = 18
const BUCKETS = 1200
/** The playhead. The one hard-coded colour: it must sit above both themes'
 *  greys and match nothing else on the page. */
const CURSOR = '#ea580c'

interface Lane {
  label: string
  /** Max |sample| per bucket, normalised so the loudest bucket is 1. */
  peaks: Float32Array
}

interface LoadedTrack {
  track: string
  buffer: AudioBuffer
}

interface Loaded {
  lanes: Lane[]
  tracks: LoadedTrack[]
  duration: number
  /** Tracks that had a READY row but whose bytes could not be fetched/decoded. */
  failed: string[]
}

/**
 * A 16-bit PCM WAV of the rendered buffer, for the Download button.
 *
 * WAV and not Opus/M4A on purpose: the page holds decoded PCM and the platform
 * ships no encoder — MediaRecorder wants a realtime stream and WebCodecs gives
 * frames with no container. A voice call at 24 kHz stereo is ~5.8 MB/min, which
 * is an acceptable price for a file every player on earth opens.
 */
function wavBlob(buf: AudioBuffer): Blob {
  const ch = buf.numberOfChannels
  const n = buf.length
  const bytes = 44 + n * ch * 2
  const dv = new DataView(new ArrayBuffer(bytes))
  const ascii = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) dv.setUint8(o + i, s.charCodeAt(i))
  }
  ascii(0, 'RIFF')
  dv.setUint32(4, bytes - 8, true)
  ascii(8, 'WAVE')
  ascii(12, 'fmt ')
  dv.setUint32(16, 16, true)
  dv.setUint16(20, 1, true)
  dv.setUint16(22, ch, true)
  dv.setUint32(24, buf.sampleRate, true)
  dv.setUint32(28, buf.sampleRate * ch * 2, true)
  dv.setUint16(32, ch * 2, true)
  dv.setUint16(34, 16, true)
  ascii(36, 'data')
  dv.setUint32(40, n * ch * 2, true)
  let o = 44
  const chans: Float32Array[] = []
  for (let c = 0; c < ch; c++) chans.push(buf.getChannelData(c))
  for (let i = 0; i < n; i++) {
    for (let c = 0; c < ch; c++) {
      const v = Math.max(-1, Math.min(1, chans[c]?.[i] ?? 0))
      dv.setInt16(o, v < 0 ? v * 32768 : v * 32767, true)
      o += 2
    }
  }
  return new Blob([dv.buffer], { type: 'audio/wav' })
}

function peaksOf(data: Float32Array): Float32Array {
  const out = new Float32Array(BUCKETS)
  const per = Math.max(1, Math.floor(data.length / BUCKETS))
  for (let b = 0; b < BUCKETS; b++) {
    let m = 0
    const start = b * per
    const end = Math.min(data.length, start + per)
    for (let i = start; i < end; i++) {
      const v = Math.abs(data[i] ?? 0)
      if (v > m) m = v
    }
    out[b] = m
  }
  let max = 0
  for (let b = 0; b < BUCKETS; b++) if ((out[b] ?? 0) > max) max = out[b] ?? 0
  if (max > 0) for (let b = 0; b < BUCKETS; b++) out[b] = (out[b] ?? 0) / max
  return out
}

function fmtTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The panel's advice, which assumes the panel's tech stats drawer exists. */
const DEFAULT_LOAD_FAILURE_HINT =
  'The players in the tech stats drawer fetch the same objects a different way and may still work.'

/** The default: the authenticated, same-origin byte stream the panel uses. */
const sameOriginTrackUrl = (sessionId: string, track: string) =>
  `/api/sessions/${sessionId}/audio/${track}/file`

export function SessionAudioPlayer({
  sessionId,
  recordings,
  onPlayhead,
  onDuration,
  loadFailureHint = DEFAULT_LOAD_FAILURE_HINT,
  trackUrl = sameOriginTrackUrl,
}: {
  sessionId: string
  recordings: PlayableRecording[]
  /** Fired ~4×/s during playback with the position in seconds; null on stop. */
  onPlayhead?: (sec: number | null) => void
  /** Where one track's bytes live. May be async — the session viewer has to ask
   *  its host for a presigned URL. Defaults to the authenticated same-origin
   *  stream; see the CORS note in this file's header before overriding it. */
  trackUrl?: (sessionId: string, track: string) => string | Promise<string>
  /**
   * The DECODED length in seconds, once every track has decoded — the longest of
   * them, which is the length this player treats as the recording's.
   *
   * Exists for the session viewer, which has no `recordings.actualMs` to anchor
   * playback against and uses this instead. It is the better number of the two
   * (measured from the samples rather than recorded alongside them); the panel
   * simply has the column already. Fired once per load, never during playback.
   */
  onDuration?: (sec: number | null) => void
  /**
   * What to suggest when NO track could be loaded, appended to "Could not load
   * the audio for X."
   *
   * A PROP RATHER THAN A CONSTANT BECAUSE THE ADVICE IS NOT UNIVERSAL. This
   * sentence used to be hard-coded to "the players in the tech stats drawer fetch
   * the same objects a different way and may still work" — true in the panel, and
   * actively misleading in the session viewer, which has no such drawer and whose
   * reader has nowhere else to look. Telling somebody to open a thing that does
   * not exist is worse than saying nothing.
   */
  loadFailureHint?: string
}) {
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clock, setClock] = useState(0)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const audioCtx = useRef<AudioContext | null>(null)
  const sources = useRef<AudioBufferSourceNode[]>([])
  /** Playback origin: position within the track at the ctx time we started. */
  const baseOffset = useRef(0)
  const startedAtCtx = useRef(0)
  const raf = useRef(0)
  const lastNotify = useRef(0)
  const playingRef = useRef(false)
  const onPlayheadRef = useRef(onPlayhead)
  onPlayheadRef.current = onPlayhead
  // Through a ref for the same reason as `onPlayhead`: a caller passing an inline
  // arrow would otherwise re-run the decode effect on every render.
  const onDurationRef = useRef(onDuration)
  onDurationRef.current = onDuration
  // Through a ref for the same reason: read inside the decode effect, which must
  // not re-run because a caller passed a new string literal.
  const hintRef = useRef(loadFailureHint)
  hintRef.current = loadFailureHint

  const playable = playableTracks(recordings)
  const playableKey = playable.map((r) => `${r.track}:${r.s3Key}`).join(',')

  // ---- load & decode -------------------------------------------------------
  useEffect(() => {
    if (playable.length === 0) return
    const ac = new AbortController()
    const ctx = new AudioContext()
    audioCtx.current = ctx
    void (async () => {
      const lanes: Lane[] = []
      const tracks: LoadedTrack[] = []
      const failed: string[] = []
      for (const rec of playable) {
        try {
          const res = await fetch(await trackUrl(sessionId, rec.track), { signal: ac.signal })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          const buf = await ctx.decodeAudioData(await res.arrayBuffer())
          tracks.push({ track: rec.track, buffer: buf })
          // ONE LANE PER LABEL `lanesFor` RETURNS, and the channel index is the
          // label's position — a carrier file gives two, anything else gives one.
          // The rule is next door so a test can check it; see audio-legs.ts for
          // what it got wrong on the first live transfer.
          lanesFor(rec.track, buf.numberOfChannels).forEach((label, ch) => {
            lanes.push({ label, peaks: peaksOf(buf.getChannelData(ch)) })
          })
        } catch {
          if (ac.signal.aborted) return
          failed.push(rec.track)
        }
      }
      if (ac.signal.aborted) return
      if (tracks.length === 0) {
        setError(`Could not load the audio for ${failed.join(', ')}. ${hintRef.current}`)
        return
      }
      const duration = Math.max(...tracks.map((t) => t.buffer.duration))
      setLoaded({ lanes, tracks, duration, failed })
      onDurationRef.current?.(duration)
    })()
    return () => {
      ac.abort()
      cancelAnimationFrame(raf.current)
      for (const s of sources.current) {
        try {
          s.stop()
        } catch {
          /* never started or already stopped */
        }
      }
      sources.current = []
      void ctx.close()
      audioCtx.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, playableKey])

  // ---- drawing -------------------------------------------------------------
  const position = (): number => {
    const ctx = audioCtx.current
    if (!ctx || !loaded) return baseOffset.current
    const pos = playingRef.current
      ? baseOffset.current + ctx.currentTime - startedAtCtx.current
      : baseOffset.current
    return Math.min(pos, loaded.duration)
  }

  const draw = () => {
    const canvas = canvasRef.current
    if (!canvas || !loaded) return
    const dpr = window.devicePixelRatio || 1
    const w = canvas.clientWidth
    const h = loaded.lanes.length * LANE_H + AXIS_H
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr)
      canvas.height = Math.round(h * dpr)
    }
    const g = canvas.getContext('2d')
    if (!g) return
    g.setTransform(dpr, 0, 0, dpr, 0, 0)
    g.clearRect(0, 0, w, h)

    // Theme-proof: the canvas inherits the page's text colour; everything else
    // is that colour at different alphas.
    const ink = getComputedStyle(canvas).color
    const pos = position()
    const progressX = loaded.duration > 0 ? (pos / loaded.duration) * w : 0

    loaded.lanes.forEach((lane, i) => {
      const top = i * LANE_H
      const mid = top + LANE_H / 2
      const amp = LANE_H / 2 - 4

      // The baseline is a grey hairline and it stays one: it is NOT darkened by
      // progress, and silence draws no bar of its own — a minimum bar height
      // turned every played pause into a near-black rule through the lane.
      g.globalAlpha = 0.15
      g.fillStyle = ink
      g.fillRect(0, mid - 0.25, w, 0.5)

      for (let x = 0; x < w; x++) {
        const p = lane.peaks[Math.min(BUCKETS - 1, Math.floor((x / w) * BUCKETS))] ?? 0
        const bar = p * amp
        if (bar < 0.5) continue
        g.globalAlpha = x <= progressX ? 0.85 : 0.35
        g.fillRect(x, mid - bar, 1, bar * 2)
      }

      g.globalAlpha = 0.5
      g.font = '10px system-ui, sans-serif'
      g.textBaseline = 'top'
      g.fillText(lane.label, 4, top + 4)
    })

    // Time axis: labelled ticks at a step the duration can afford, minor ticks
    // at half-steps. Seconds under two minutes, m:ss beyond.
    const axisTop = loaded.lanes.length * LANE_H
    const step = loaded.duration <= 60 ? 10 : loaded.duration <= 180 ? 30 : loaded.duration <= 900 ? 60 : 300
    g.font = '10px ui-monospace, monospace'
    for (let t = step / 2; t < loaded.duration; t += step / 2) {
      const x = (t / loaded.duration) * w
      const major = Math.abs(t / step - Math.round(t / step)) < 1e-6
      g.globalAlpha = major ? 0.6 : 0.3
      g.fillRect(x, axisTop + 2, 1, major ? 8 : 5)
      if (major) {
        g.globalAlpha = 0.6
        g.fillText(loaded.duration <= 120 ? String(Math.round(t)) : fmtTime(t), x + 3, axisTop + 4)
      }
    }

    g.globalAlpha = 1
    g.fillStyle = CURSOR
    g.fillRect(progressX - 0.5, 0, 1.5, axisTop)
  }

  // Redraw on load, on resize, and (below) per animation frame while playing.
  useEffect(() => {
    if (!loaded) return
    draw()
    const canvas = canvasRef.current
    if (!canvas) return
    const ro = new ResizeObserver(() => draw())
    ro.observe(canvas)
    return () => ro.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded])

  // ---- playback ------------------------------------------------------------
  const stopSources = () => {
    for (const s of sources.current) {
      s.onended = null
      try {
        s.stop()
      } catch {
        /* never started */
      }
    }
    sources.current = []
  }

  const tick = () => {
    if (!playingRef.current) return
    const pos = position()
    draw()
    const now = performance.now()
    if (now - lastNotify.current > 250) {
      lastNotify.current = now
      setClock(pos)
      onPlayheadRef.current?.(pos)
    }
    if (loaded && pos >= loaded.duration) {
      pause(loaded.duration)
      return
    }
    raf.current = requestAnimationFrame(tick)
  }

  const play = (from?: number) => {
    const ctx = audioCtx.current
    if (!ctx || !loaded) return
    stopSources()
    let offset = from ?? baseOffset.current
    if (offset >= loaded.duration) offset = 0
    void ctx.resume()
    sources.current = loaded.tracks
      .map((t) => t.buffer)
      .filter((b) => offset < b.duration)
      .map((b) => {
        const src = ctx.createBufferSource()
        src.buffer = b
        src.connect(ctx.destination)
        src.start(0, offset)
        return src
      })
    baseOffset.current = offset
    startedAtCtx.current = ctx.currentTime
    playingRef.current = true
    setPlaying(true)
    cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(tick)
  }

  const pause = (at?: number) => {
    const pos = at ?? position()
    stopSources()
    cancelAnimationFrame(raf.current)
    baseOffset.current = pos
    playingRef.current = false
    setPlaying(false)
    setClock(pos)
    onPlayheadRef.current?.(null)
    draw()
  }

  const seek = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!loaded) return
    const rect = e.currentTarget.getBoundingClientRect()
    const t = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * loaded.duration
    if (playingRef.current) play(t)
    else {
      baseOffset.current = t
      setClock(t)
      draw()
    }
  }

  /**
   * The compiled recording as ONE stereo file: caller on the left channel,
   * assistant on the right (a telephony file already has that shape and passes
   * through). Rendered offline at 24 kHz from the decoded buffers, aligned at
   * zero exactly like playback — verified against an independent phone
   * recording of a real call to sit within ~60 ms of reality.
   */
  const download = async () => {
    if (!loaded || saving) return
    setSaving(true)
    try {
      const rate = 24_000
      const off = new OfflineAudioContext(2, Math.ceil(loaded.duration * rate), rate)
      const merger = off.createChannelMerger(2)
      merger.connect(off.destination)
      for (const { track, buffer } of loaded.tracks) {
        const src = off.createBufferSource()
        src.buffer = buffer
        // Every two-party file passes through as it is: the carrier's, a
        // transfer's, and a takeover's (caller left, the person right).
        if (isTwoPartyFile(track, buffer.numberOfChannels)) {
          const split = off.createChannelSplitter(2)
          src.connect(split)
          split.connect(merger, 0, 0)
          split.connect(merger, 1, 1)
        } else {
          src.connect(merger, 0, track === 'assistant' ? 1 : 0)
        }
        src.start(0)
      }
      const url = URL.createObjectURL(wavBlob(await off.startRendering()))
      const a = document.createElement('a')
      a.href = url
      a.download = `recording-${sessionId}.wav`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } finally {
      setSaving(false)
    }
  }

  if (playable.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No playable recording yet — the transcript below stands on its own.
      </p>
    )
  }
  if (error) return <p className="err text-xs">{error}</p>

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Audio recording</span>
        <Button
          size="sm"
          disabled={!loaded}
          onClick={() => (playing ? pause() : play())}
          className="cursor-pointer"
        >
          {playing ? <PauseIcon className="size-3.5" aria-hidden /> : <PlayIcon className="size-3.5" aria-hidden />}
          {playing ? 'Pause' : 'Play'}
        </Button>
        {loaded && (
          <span className="num text-xs text-muted-foreground">
            {fmtTime(clock)} / {fmtTime(loaded.duration)}
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={!loaded || saving}
          onClick={() => void download()}
          className="cursor-pointer"
          title="One stereo WAV: caller on the left channel, assistant on the right."
        >
          <DownloadIcon className="size-3.5" aria-hidden />
          {saving ? 'Preparing…' : 'Download'}
        </Button>
        {loaded && loaded.failed.length > 0 && (
          <span className="text-xs text-ev-warn">
            the {loaded.failed.join(' and ')} track could not be loaded and is not drawn
          </span>
        )}
      </div>
      {loaded ? (
        <canvas
          ref={canvasRef}
          onClick={seek}
          className="mt-2 w-full cursor-pointer text-foreground"
          style={{ height: loaded.lanes.length * LANE_H + AXIS_H }}
          aria-label="Recording waveform. Click to seek."
        />
      ) : (
        <div className="mt-2 animate-pulse rounded-md bg-muted" style={{ height: LANE_H * 2 + AXIS_H }} />
      )}
    </div>
  )
}
